import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

export default function OrganismSearch({
    value = null,
    onChange,
    placeholder = "Search organism...",
}) {
    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);

    const wrapperRef = useRef(null);

    const [organisms, setOrganisms] = useState([]);
    const [loading, setLoading] = useState(false);

    // Display selected organism
    useEffect(() => {
        if (!value) {
            setQuery("");
            return;
        }

        if (typeof value === "string") {
            setQuery(value);
            return;
        }

        setQuery(value.renderName || value.name || "");
    }, [value]);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);

        return () =>
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
    }, []);

    // Search
    useEffect(() => {
        const search = query.trim();

        if (!isOpen || search.length < 3) {
            setLoading(false);
            setOrganisms([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                setLoading(true);

                const response = await api.get("/organisms/search", {
                    params: {
                        q: search,
                        limit: 20,
                    },
                });

                setOrganisms(response.data.results || []);
            } catch (error) {
                console.error("Failed to search organisms:", error);
                setOrganisms([]);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [query, isOpen]);

    return (
        <div className="relative" ref={wrapperRef}>
            <input
                type="text"
                value={query}
                placeholder={placeholder}
                autoComplete="off"
                onFocus={() => setIsOpen(true)}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setIsOpen(true);

                    if (onChange) {
                        onChange(null);
                    }
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#0B6936] focus:outline-none focus:ring-2 focus:ring-[#EAF4E3]"
            />

            {isOpen && (loading || query.trim().length >= 3) && (
                <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">

                    {loading && (
                        <div className="px-4 py-3 text-sm text-gray-500">
                            Searching...
                        </div>
                    )}

                    {!loading && organisms.length === 0 && (
                        <div className="px-4 py-3 text-sm text-gray-500">
                            No organisms found.
                        </div>
                    )}

                    {!loading &&
                        organisms.map((organism) => (
                            <div
                                key={organism.GCCID || organism.name}
                                className="flex items-center justify-between border-b border-gray-100 px-4 py-3 hover:bg-green-50"
                            >
                                <button
                                    type="button"
                                    onClick={() => {
                                        setQuery(organism.renderName);
                                        setIsOpen(false);

                                        if (onChange) {
                                            onChange(organism);
                                        }
                                    }}
                                    className="flex-1 text-left"
                                >
                                    <span className="font-medium text-gray-900">
                                        {organism.renderName}
                                    </span>
                                </button>

                                <a
                                    href={organism.hyperLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="ml-4 text-sm font-medium text-[#0B6936] hover:underline"
                                >
                                    Know More ↗
                                </a>
                            </div>
                        ))}
                </div>
            )}  </div>
    );
}